import React, {
    ReactElement, useEffect, useState,
} from "react";
import { AiOutlineEdit, AiOutlinePlus } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { Form } from "react-bootstrap";
import {
    GetUserAgentsDocument,
    Permission,
    UserRole,
    useCreateTokenMutation,
    useDeleteTokenMutation,
    useGetUserAgentsQuery, useSetUserRoleMutation,
} from "../../../../__generated__/api";
import { Loading } from "../../../../components";
import Avatar from "../../../../components/avatar/Avatar";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import DisplayUserGroup from "../../../../components/creators/user-groups/UserGroups";
import { Filters } from "../../../../components/filters/Filters";
import Users from "../../../../components/pickers/users/Users";
import Select from "../../../../components/select/Select";
import useArticleContext from "../../../../contexts/HelpContext";
import useRouting, { Link, Paths, useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/session";
import { ErrorRemote } from "../../../error/Error";
import Time from "../../../../components/time/Time";
import Confirm from "../../../../components/confirm/Confirm";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import usePortal from "../../../../contexts/PortalContext";

const ROLE_TO_NAME = {
    [UserRole.Administrator]: "Administrator",
    [UserRole.Agent]: "Agent",
    [UserRole.Guest]: "Guest",

};

const ROLE_OPTIONS = [
    {
        label: ROLE_TO_NAME[UserRole.Administrator],
        value: UserRole.Administrator,
    },
    {
        label: ROLE_TO_NAME[UserRole.Agent],
        value: UserRole.Agent,
    },
];

const PERMISSION_MAP: Record<string, {name: string; description: string}> = {
    [Permission.Administrator]: {
        name: "Administrator",
        description: "Full administrator with access to everything available on the account.",
    },
    [Permission.ArticleModify]: {
        name: "Modify Articles",
        description: "Modify all articles",
    },
    [Permission.ArticleRead]: {
        name: "Read all Articles",
        description: "Read all articles published or not",
    },
    [Permission.ArticleReadPublic]: {
        name: "Read public articles",
        description: "Read all public, published articles",
    },
    [Permission.ChatsModify]: {
        name: "Manage chats",
        description: "Manage conversations with customers",
    },
    [Permission.ChatsModifyAll]: {
        name: "Manage all chats",
        description: "Manage all conversations with customers",
    },
    [Permission.ChatsRead]: {
        name: "Read chats",
        description: "Read Conversations with customers",
    },
    [Permission.ChatsReadAll]: {
        name: "Read all chats",
        description: "Read all conversations with customers",
    },
    [Permission.GroupsRead]: {
        name: "Read Groups",
        description: "Read groups",
    },
    [Permission.GroupsWrite]: {
        name: "Write Groups",
        description: "Create/Write groups",
    },
    [Permission.ReadIntegration]: {
        name: "Read Integrations",
        description: "View current Integrations",
    },
    [Permission.RegisterIntegration]: {
        name: "Install integrations",
        description: "Install new integrations",
    },
    [Permission.Reports]: {
        name: "View Reports",
        description: "View reports APIs via token",
    },
    [Permission.TicketsComment]: {
        name: "Comment on Tickets",
        description: "Comment and message tickets",
    },
    [Permission.TicketsModify]: {
        name: "Modify all Tickets",
        description: "Manager and modify all tickets",
    },
    [Permission.TicketsRead]: {
        name: "Read Tickets",
        description: "Read tickets.",
    },
    [Permission.TicketsReadAll]: {
        name: "Read all Tickets",
        description: "Read all tickets",
    },
    [Permission.TicketsReadSelf]: {
        name: "Read self Tickets",
        description: "Read tickets created by the current user",
    },
    [Permission.TicketCreate]: {
        name: "Create Tickets",
        description: "Create Tickets",
    },
    [Permission.TicketCreateGuest]: {
        name: "Create Tickets (Guest)",
        description: "Create tickets as a guest user.",
    },
    [Permission.TicketStateModify]: {
        name: "Modify Columns",
        description: "Manage columns and states.",
    },
    [Permission.UsersCreate]: {
        name: "Create Users",
        description: "Create new users",
    },
    [Permission.UsersRead]: {
        name: "Read users",
        description: "View all users",
    },
    [Permission.UsersWrite]: {
        name: "Modify Users",
        description: "Manage and modify users",
    },

};

const FEATURE_MANAGE_TOKENS = "frontend.manage.developer.tokens";

interface DeleteUserState {
    id: string;
    name: string;
}
function DisplayList(): ReactElement {
    const {
        data, error, loading: isLoading, refetch,
    } = useGetUserAgentsQuery({
        fetchPolicy: "network-only",
    });
    const { featureFlags } = usePortal();

    const [saveUserRole] = useSetUserRoleMutation();
    const [deleteToken] = useDeleteTokenMutation();

    const [currentUsers, setCurrentUsers] = useState<string[]>();
    const [addingRole, setAddingRole] = useState<UserRole>(UserRole.Agent);

    const [filterRoles, setFilterRoles] = useState(new Set<string>());
    const [userSearch, setUserSearch] = useState<string>();
    const [editUser, setEditUser] = useState<{ id: string; role: UserRole }>();
    const [editRole, setEditRole] = useState<UserRole>();

    const [isShowTokenCreate, setShowTokenCreate] = useState(false);
    const [createPermissions, setCreatePermissions] = useState<Permission[]>([]);
    const [tokenName, setTokenName] = useState<string>("");

    const currentSession = useCurrentSession();

    useEffect(() => {
        if (editUser) {
            setEditRole(editUser.role);
        }
    }, [editUser]);

    const setUsersRole = async (ids: string[], role: UserRole) => {
        await saveUserRole({
            variables: {
                ids,
                role,
            },
        });
        refetch();
    };

    const deleteConfirm = useDeleteConfirm<DeleteUserState>(
        item => setUsersRole([item.id], UserRole.Guest),
        item => item.name,
        "Remove User Association",
        "Are you sure you want to remove all roles for",
    );

    const deleteTokenConfirm = useDeleteConfirm<DeleteUserState>(
        async item => {
            await deleteToken({
                refetchQueries: [
                    { query: GetUserAgentsDocument },
                ],
                variables: {
                    id: item.id,
                },
            });
        },
        item => item.name,
        "Delete Token",
        "Are you sure you want to delete and invalidate the token",
    );

    const [createToken] = useCreateTokenMutation();

    const [createdToken, setCreatedToken] = useState("");

    const processCreateToken = async () => {
        const tokenResponse = await createToken({
            refetchQueries: [
                { query: GetUserAgentsDocument },
            ],
            variables: {
                token: {
                    permissions: createPermissions,
                    name: tokenName,
                },
            },
        });

        setCreatedToken(tokenResponse.data?.createToken.token as string);
    };

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }
    let users = data?.users;
    if (filterRoles && filterRoles.size > 0) {
        users = users?.filter(user => user.role && filterRoles.has(user.role));
    }
    if (userSearch) {
        const search = userSearch.toLocaleLowerCase();
        users = users?.filter(user => user.name?.toLocaleLowerCase().includes(search));
    }

    const purchasedCount = data?.portal?.billing?.isSetup ? data?.portal?.billing?.purchasedUserCount : undefined;
    const isOver = purchasedCount && purchasedCount < (data?.users?.length ?? 0);

    const selectedPermissionsSet = new Set(createPermissions);

    return (
        <div className="d-flex align-items-stretch flex-column">
            {deleteConfirm.element}
            {deleteTokenConfirm.element}

            <div className="border p-3 rounded bg-white">
                {data?.users?.isNotEmpty ? (
                    <>
                        <div className="mb-3">
                            <h5 className="text-light mb-0">
                                Users
                            </h5>
                            {purchasedCount && (
                                <div className={isOver ? "alert alert-danger my-3" : "small"}>
                                    {isOver
                                        ? "You have 0 seats remaining."
                                        : (
                                            <>
                                                You are using
                                                {" "}
                                                {data?.users?.length}
                                                {" "}
                                                of
                                                {" "}
                                                {purchasedCount}
                                                {" "}
                                                purchased seats.
                                            </>
                                        )}
                                    {" "}
                                    <Link
                                        to={Paths.ADMINISTRATOR_SETTING}
                                        params={{
                                            setting: "billing",
                                        }}
                                        className="text-decoration-none"
                                    >
                                        Buy more.
                                    </Link>
                                </div>
                            )}
                        </div>
                        <Filters
                            selectedCategories={filterRoles}
                            onSelected={setFilterRoles}
                            categories={[
                                {
                                    value: UserRole.Agent,
                                    label: ROLE_TO_NAME[UserRole.Agent],
                                },
                                {
                                    value: UserRole.Administrator,
                                    label: ROLE_TO_NAME[UserRole.Administrator],
                                },
                            ]}
                            search={userSearch}
                            onSetSearch={setUserSearch}
                        />
                        <div className="list-group">
                            {users?.sortBy(user => user.name?.toLowerCase()).map(user => (

                                <div
                                    key={user.id}
                                    className="d-flex w-100 justify-content-between list-group-item align-items-center"
                                >
                                    <div className="d-flex flex-grow-1 align-items-center">
                                        <div>
                                            <Avatar
                                                src={user.avatarUrl as string}
                                                isCircle
                                                variant="xs"
                                            />
                                            {user.name}
                                        </div>
                                        {editUser?.id === user.id && editUser ? (
                                            <>
                                                <Select
                                                    className="mx-3 col-3"
                                                    minRequired={1}
                                                    options={ROLE_OPTIONS}
                                                    value={!editRole ? undefined : {
                                                        label: ROLE_TO_NAME[editRole],
                                                        value: editRole,
                                                    }}
                                                    onChange={role => setEditRole(role.value as UserRole)}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm p-2"
                                                    onClick={() => {
                                                        setUsersRole([editUser.id], editRole ?? UserRole.Guest);
                                                        setEditUser(undefined);
                                                    }}
                                                >
                                                    Save
                                                </button>
                                            </>
                                        ) : (
                                            <small className="text-light ms-3">
                                                {ROLE_TO_NAME[user.role as keyof typeof ROLE_TO_NAME]}
                                            </small>
                                        )}

                                    </div>

                                    {currentSession.userId !== user.id && (
                                        <div className="ms-2 text-light">
                                            <button
                                                className="btn btn-none text-gray-500 p-0 me-3"
                                                type="button"
                                                aria-label="Edit user"
                                                onClick={() => setEditUser({
                                                    id: user.id,
                                                    role: user.role as UserRole,
                                                })}
                                            >
                                                <AiOutlineEdit />
                                            </button>
                                            <button
                                                className="btn btn-none text-gray-500 p-0"
                                                type="button"
                                                aria-label="Delete user"
                                                onClick={() => deleteConfirm.setDeleting({
                                                    id: user.id,
                                                    name: user.name as string,
                                                })}
                                            >
                                                <BsTrash />
                                            </button>
                                        </div>
                                    )}

                                </div>

                            ))}

                        </div>

                    </>
                ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center text-light my-4">
                        <h5 className="text-light">
                            Users
                        </h5>

                        <small>
                            No users selected
                        </small>
                    </div>
                )}
                <div className="pt-4">
                    <h5 className="text-light mb-0 mt-4 mb-2">
                        Add a user
                    </h5>
                    <small className="text-light mb-1 d-block">
                        To assign a role to a user, select the users below, specify the role,
                        and click &quot;Add&quot;.
                    </small>
                    <form
                        className="d-flex justify-content-between align-items-center"
                        onSubmit={evt => {
                            evt.preventDefault();
                            if (!currentUsers?.isNotEmpty || !addingRole) {
                                return;
                            }
                            setUsersRole(currentUsers, addingRole);
                            setAddingRole(UserRole.Agent);
                            setCurrentUsers([]);
                        }}
                    >
                        <div className="d-flex col align-items-center">
                            <span className="mx-2 text-light">
                                Users:
                            </span>
                            <Users
                                minRequired={1}
                                onSelected={user => setCurrentUsers(user)}
                                value={currentUsers}
                                className="w-100"
                                isMulti
                            />
                        </div>

                        <div className="d-flex col-3 align-items-center">
                            <span className="mx-2 text-light">
                                Role:
                            </span>
                            <Select
                                className="w-100"
                                minRequired={1}
                                options={ROLE_OPTIONS}
                                onChange={role => setAddingRole(role.value as UserRole)}
                                value={!addingRole ? undefined : {
                                    value: addingRole,
                                    label: ROLE_TO_NAME[addingRole],
                                }}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary py-2 col-1 ms-2"
                        >
                            Add
                        </button>
                    </form>
                </div>

            </div>

            <Confirm
                shouldShow={isShowTokenCreate}
                title="Create a new token"
                onHide={() => {
                    setShowTokenCreate(false);
                    setCreatedToken("");
                    setTokenName("");
                    setCreatePermissions([]);
                }}
                onConfirm={!createdToken ? processCreateToken : undefined}
                confirmText={!createdToken ? "Create Token" : undefined}
                size={createdToken ? "lg" : "xl"}
            >
                {createdToken ? (

                    <div className="">
                        <p className="mb-2">
                            This token will only display here now. Once you close this screen, you will be unable to be
                            retrieved again without creating a new token. Please store it in a safe space.
                        </p>
                        <CodeBlock code={createdToken} isCopyable size="sm" />

                    </div>

                ) : (

                    <div className="d-flex">

                        <Form.Group>
                            <Form.Label>
                                Name
                            </Form.Label>
                            <Form.Control
                                value={tokenName}
                                onChange={evt => setTokenName(evt.target.value)}
                            />
                            <Form.Text>
                                What is the name of the token you want to store?
                            </Form.Text>
                        </Form.Group>

                        <div className="list-group list-group-flush flex-grow-1 ms-3 ps-3 border-start">

                            <Form.Label>
                                Scopes
                            </Form.Label>
                            {Object.entries(PERMISSION_MAP).map(([key, value]) => (
                                <div className="list-group-item px-0 d-flex justify-content-between align-items-center" key={key}>
                                    <div>
                                        <div>{value.name}</div>
                                        <small>
                                            {value.description}
                                        </small>
                                    </div>
                                    <Form.Check
                                        type="switch"
                                        name={`picker-${key}`}
                                        checked={selectedPermissionsSet.has(key as Permission)}
                                        onChange={evt => {
                                            if (evt.target.checked) {
                                                setCreatePermissions([...createPermissions, key as Permission]);
                                            } else {
                                                setCreatePermissions(createPermissions.filter(row => row !== key));
                                            }
                                        }}
                                    />

                                </div>
                            ))}
                        </div>

                    </div>
                )}
            </Confirm>
            {featureFlags.has(FEATURE_MANAGE_TOKENS) && (
                <div className="border p-3 rounded bg-white mt-4">
                    {data?.tokens?.isNotEmpty ? (
                        <>
                            <div className="mb-3 d-flex justify-content-between">
                                <h5 className="text-light mb-0">
                                    Tokens
                                </h5>
                                <button
                                    type="button"
                                    className="btn btn-none btn-sm"
                                    onClick={() => setShowTokenCreate(true)}
                                >
                                    <AiOutlinePlus />
                                    {" "}
                                    Create Token
                                </button>
                            </div>
                            <div className="list-group">
                                {data?.tokens?.sortBy(user => user.name?.toLowerCase()).map(token => (

                                    <div
                                        key={token.id}
                                        className="d-flex w-100 justify-content-between list-group-item align-items-center"
                                    >
                                        <div className="d-flex flex-column">
                                            <div>
                                                {token.name}
                                            </div>

                                            <small>
                                                Created:
                                                {" "}
                                                <Time date={token.createdDate} variant="date" />
                                                {" "}
                                                <Time date={token.createdDate} variant="time" />
                                            </small>
                                            <small>
                                                Scopes:
                                                {" "}
                                                {(token.permissions ?? [])
                                                    .map(row => PERMISSION_MAP[row]?.name)?.filterNull().join(", ")}
                                            </small>
                                        </div>

                                        <div className="ms-2 text-light">
                                            <button
                                                className="btn btn-none text-gray-500 p-0"
                                                type="button"
                                                aria-label="Deactivates a token"
                                                onClick={() => deleteTokenConfirm.setDeleting({
                                                    id: token.id,
                                                    name: token.name as string,
                                                })}
                                            >
                                                <BsTrash />
                                            </button>
                                        </div>

                                    </div>

                                ))}

                            </div>

                        </>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center text-light my-4">
                            <h5 className="text-light">
                                Tokens
                            </h5>
                            <small className="text-gray-500">
                                Security tokens allow you to manage Foqal using the Foqal API. For information, view our
                                {" "}
                                <a href="https://developer.support.foqal.io/" target="_blank" rel="noreferrer">
                                    developer documentation
                                </a>
                            </small>
                            <button
                                type="button"
                                onClick={() => setShowTokenCreate(true)}
                                className="btn btn-secondary btn-sm my-3"
                            >
                                <AiOutlinePlus />
                                {" "}
                                Token
                            </button>

                        </div>
                    )}

                </div>
            )}

        </div>
    );
}

export default function UsersSettings(): ReactElement {
    const { historyPush } = useRouting();
    useArticleContext("urn:fq:article:31a8fea042ce453c8dcf04b38a3f6ed6");

    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    return id ? (
        <DisplayUserGroup
            id={id}
            canDelete
            className="border p-3 rounded mb-4 bg-white"
            onDeleted={() => {
                historyPush(Paths.ADMINISTRATOR_SETTING, {
                    setting: "user-groups",
                });
            }}
            onSave={() => {
                historyPush(Paths.ADMINISTRATOR_SETTING, {
                    setting: "user-groups",
                });
            }}
        />
    ) : <DisplayList />;
}
